import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import {
  snackbar,
  getStateSingleAutocompleteInitialValues,
  getTimezoneAutocompleteInitialValues,
} from '@occc-app/design-system';
import {
  Change_Source,
  Inbound_Message_Class,
  LocationAttributeCreate,
  LocationUpdate,
  SiteManagementLocationFragment,
  User_Role,
} from '@occc/graphql';
import { useLocationUpdateMutation } from '@occc-app/dataAccessLayer/location/mutations';
import { useGetLocationSummaryQuery } from '@occc-app/dataAccessLayer/location/queries';
import { useSiteDisconnect } from '@occc-app/dataAccessLayer/site/mutations';
import { onErrorDefault } from '@occc-app/dataAccessLayer/utils';
import { useUserSelector } from '@occc-app/App/AppContext';

import LocationProvider, { LocationContext } from './LocationProvider';
import LocationUpdatePage from './LocationUpdatePage';
import { LocationUpdateFormData } from './types';

import { routeCreator, tabValues } from '../utils';
import { LOCATION_ATTRIBUTE_VALUE } from '../constants';

const mapInitialValues = (location: SiteManagementLocationFragment | null) => {
  if (!location) {
    return {};
  }

  const address = {
    ...location.address,
    state:
      location.address && location.address.state
        ? getStateSingleAutocompleteInitialValues(location.address.state)
        : null,
    geoLocation:
      location.address && location.address.geoLocation
        ? {
            latitude: location.address.geoLocation.latitude
              ? location.address.geoLocation.latitude.toString()
              : null,
            longitude: location.address.geoLocation.longitude
              ? location.address.geoLocation.longitude.toString()
              : null,
          }
        : null,
  };
  const siteSystemConfiguration =
    location.siteSystemConfiguration && location.siteSystemConfiguration.length
      ? location.siteSystemConfiguration[0]
      : undefined;

  const attributes =
    location.attributes && location.attributes.length
      ? location.attributes
          .filter((attribute) => attribute.value === LOCATION_ATTRIBUTE_VALUE.YES)
          .map((attribute) => attribute.name)
      : [];
  return {
    altriaRcn: location.altriaRcn?.trim() || null,
    altriaMan: location.altriaMan?.trim() || null,
    rjrAccountNumber: location.rjrAccountNumber?.trim() || null,
    siteSystemConfiguration,
    locationGroups: location?.locationGroups || [],
    address,
    attributes,
    timezone: getTimezoneAutocompleteInitialValues(location.timezone),
    payAtPumpEnabled: location.payAtPumpEnabled,
  };
};

const LocationUpdatePageContainer: React.FC = () => {
  const { locationId = '' } = useParams<{ locationId: string }>();
  const { data: locationSummary } = useGetLocationSummaryQuery(locationId);
  const siteSystemIdentifier =
    locationSummary?.location?.siteSystemConfiguration?.[0]?.identifier ?? '';
  const user = useUserSelector();
  const userRole = user?.userRole;
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const locationUpdateMutation = useLocationUpdateMutation(
    locationId,
    (locationId: string, values: LocationUpdateFormData) => {
      const attributes: LocationAttributeCreate[] = [];
      if (values.attributes && values.attributes.length) {
        values.attributes.forEach((attr) => {
          attributes.push({ name: attr.toString(), value: LOCATION_ATTRIBUTE_VALUE.YES });
        });
      }
      const res: LocationUpdate = {
        uuid: locationId,
        changeSource: Change_Source.CommandCenter,
        altriaMan: values.altriaMan?.trim() || null,
        altriaRcn: values.altriaRcn?.trim() || null,
        rjrAccountNumber: values.rjrAccountNumber?.trim() || null,
        locationGroupUuids: values?.locationGroups?.map((group) => group?.uuid) || null,
        siteSystemConfiguration: [
          {
            uuid: values.siteSystemConfiguration.uuid,
            identifier: values.siteSystemConfiguration.identifier,
            hardwareVendor: values.siteSystemConfiguration.hardwareVendor,
            loyaltyEnabled: values.siteSystemConfiguration.loyaltyEnabled,
            mobileEnabled: values.siteSystemConfiguration.mobileEnabled,
            loyaltyHeartbeatsOnlyEnabled:
              values.siteSystemConfiguration.loyaltyHeartbeatsOnlyEnabled,
          },
        ],
        address: {
          street1: values.address.street1,
          street2: values.address.street2 || null,
          city: values.address.city,
          zipCode: values.address.zipCode,
          state: values.address.state.id,
          countryCode: values.address.countryCode,
          geoLocation:
            values.address.geoLocation &&
            values.address.geoLocation.latitude &&
            values.address.geoLocation.longitude
              ? {
                  latitude: parseFloat(values.address.geoLocation.latitude.toString()),
                  longitude: parseFloat(values.address.geoLocation.longitude.toString()),
                }
              : {
                  // API sets `geoLocation` to null when lat and long is 0
                  latitude: 0,
                  longitude: 0,
                },
        },
        attributes,
        timezone: values.timezone.name,
        payAtPumpEnabled: values.payAtPumpEnabled,
      };

      return res;
    },
    (data) => {
      const uuid = data?.locationUpdate?.uuid;
      if (!uuid) return;

      const isScanDataEdit = pathname.endsWith(`${tabValues.scanData}/update`);
      isScanDataEdit
        ? navigate(routeCreator.scanData(uuid), { replace: true })
        : navigate(routeCreator.generalDetails(uuid), { replace: true });

      snackbar.success('Location was successfully updated');
    },
    onErrorDefault,
  );

  const [disconnectSiteMutation, { loading: isDisconnectMobileOrLoyaltyInProgress }] =
    useSiteDisconnect({
      refetchQueries: ['siteManagementLocation'],
    });

  const handleDisconnect = React.useCallback(
    async (
      siteSystemIdentifier: string,
      messageClass: Inbound_Message_Class,
      connectionType: string,
    ) => {
      if (!siteSystemIdentifier) {
        snackbar.error(
          'Cannot disconnect: this location has no site system identifier configured.',
        );
        return;
      }
      await disconnectSiteMutation({
        variables: {
          input: {
            siteId: siteSystemIdentifier,
            inboundMessageClass: messageClass,
          },
        },
        onCompleted: () => {
          snackbar.success(
            `This site has been disconnected from ${connectionType}. It should reconnect on its own.`,
          );
        },
        onError: onErrorDefault,
      }).catch(() => {
        // onError already surfaced the snackbar; swallow the rejected promise
        // so fire-and-forget callers don't trigger an unhandled rejection.
      });
    },
    [disconnectSiteMutation],
  );

  const handleDisconnectMobile = React.useCallback(
    () => handleDisconnect(siteSystemIdentifier, Inbound_Message_Class.Mobile, 'mobile'),
    [handleDisconnect, siteSystemIdentifier],
  );

  const handleDisconnectLoyalty = React.useCallback(
    () => handleDisconnect(siteSystemIdentifier, Inbound_Message_Class.Loyalty, 'loyalty'),
    [handleDisconnect, siteSystemIdentifier],
  );

  return (
    <LocationProvider locationId={locationId}>
      <LocationContext.Consumer>
        {(value) => {
          return (
            <LocationUpdatePage
              locationId={locationId}
              locationUpdate={(input) => {
                locationUpdateMutation(input);
              }}
              disconnectMobile={handleDisconnectMobile}
              disconnectLoyalty={handleDisconnectLoyalty}
              canDisconnect={!!siteSystemIdentifier}
              isDisconnectMobileOrLoyaltyInProgress={isDisconnectMobileOrLoyaltyInProgress}
              initialValues={mapInitialValues(value.location)}
              editable={userRole === User_Role.Admin}
              isInitiallyLoading={value.loading && !value.location}
              location={value.location}
            />
          );
        }}
      </LocationContext.Consumer>
    </LocationProvider>
  );
};

export default LocationUpdatePageContainer;
